import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Panel } from "@/components/Panel";
import { toast } from "sonner";
import {
  Link as LinkIcon, Copy, Plus, X, Eye, AlertTriangle, Trash2, RefreshCw,
  ShieldCheck, Mail, Building2, CheckCircle2, ChevronRight,
} from "lucide-react";

// Phase 36.5 — Per-investor pitch-deck magic links admin

const PUBLIC_BASE = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
};

const STATUS_COLOR = {
  active: "border-[#00E676]/40 text-[#00E676] bg-[#00E676]/5",
  revoked: "border-zinc-700 text-zinc-500 bg-zinc-900/40",
  expired: "border-[#FF6B47]/40 text-[#FF6B47] bg-[#FF6B47]/5",
  exhausted: "border-[#FFB000]/40 text-[#FFB000] bg-[#FFB000]/5",
};

function deriveStatus(link) {
  if (link.status === "revoked") return "revoked";
  if (link.expires_at && new Date(link.expires_at) < new Date()) return "expired";
  if ((link.view_count || 0) >= (link.max_views || 1)) return "exhausted";
  return "active";
}

export default function PitchLinksAdmin() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState({
    recipient_name: "",
    recipient_email: "",
    recipient_company: "",
    notes: "",
    expires_in_days: 7,
    max_views: 5,
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/pitch-deck-links");
      setLinks(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load links");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.recipient_name || !form.recipient_email) {
      toast.error("Recipient name + email are required");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/admin/pitch-deck-links", form);
      const url = `${PUBLIC_BASE}/pitch/${r.data.token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success(`Link created and copied: ${form.recipient_name}`);
      } catch (e) {
        toast.success(`Link created for ${form.recipient_name}`);
      }
      setShowForm(false);
      setForm({
        recipient_name: "", recipient_email: "", recipient_company: "",
        notes: "", expires_in_days: 7, max_views: 5,
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create link");
    } finally {
      setSubmitting(false);
    }
  };

  const copyUrl = async (token, name) => {
    const url = `${PUBLIC_BASE}/pitch/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Copied link for ${name}`);
    } catch (e) {
      toast.error("Couldn't copy. URL: " + url);
    }
  };

  const revoke = async (token, name) => {
    if (!window.confirm(`Revoke the link for ${name}? They'll see a "revoked" message immediately.`)) return;
    try {
      await api.post(`/admin/pitch-deck-links/${token}/revoke`);
      toast.success(`Revoked: ${name}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    }
  };

  const remove = async (token, name) => {
    if (!window.confirm(`Permanently DELETE the link for ${name}? This wipes the audit log too. Use Revoke instead unless you really need it gone.`)) return;
    try {
      await api.delete(`/admin/pitch-deck-links/${token}`);
      toast.success(`Deleted: ${name}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const summary = useMemo(() => {
    const total = links.length;
    const active = links.filter(l => deriveStatus(l) === "active").length;
    const totalViews = links.reduce((s, l) => s + (l.view_count || 0), 0);
    return { total, active, totalViews };
  }, [links]);

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="pitch-links-admin-page">
      {/* HEADER */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            // INVESTOR DECK · ACCESS CONTROL
          </div>
          <h1 className="font-heading font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-1">
            Pitch Deck — Magic Links
          </h1>
          <p className="text-xs text-zinc-500 max-w-xl mt-1.5">
            Mint a unique URL per investor. Each link generates a watermarked
            PDF stamped with the recipient's email + access date — leaked
            screenshots are traceable. Revoke any link at any time.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            disabled={loading}
            data-testid="pitch-links-refresh"
            className="border border-zinc-800 hover:border-zinc-700 px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-300 flex items-center gap-2"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            REFRESH
          </button>
          <button
            onClick={() => setShowForm(s => !s)}
            data-testid="pitch-links-new-btn"
            className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-4 py-2 hover:opacity-90 flex items-center gap-2"
          >
            <Plus className="w-3.5 h-3.5" /> NEW LINK
          </button>
        </div>
      </div>

      {/* SUMMARY STRIP */}
      <div className="grid grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
        <Stat label="ACTIVE LINKS" value={summary.active} accent="green" sub={`OF ${summary.total} TOTAL`} />
        <Stat label="TOTAL VIEWS" value={summary.totalViews} accent="amber" />
        <Stat label="MASTER COPY" value="ADMIN ONLY" accent="white" sub="UN-WATERMARKED" />
      </div>

      {/* NEW LINK FORM */}
      {showForm && (
        <Panel title="// MINT NEW LINK" testId="pitch-links-form-panel">
          <form onSubmit={submit} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="RECIPIENT NAME *" testid="pl-recipient-name"
              value={form.recipient_name}
              onChange={v => setForm(f => ({ ...f, recipient_name: v }))}
              placeholder="Marc Andreessen" />
            <Input label="RECIPIENT EMAIL *" type="email" testid="pl-recipient-email"
              value={form.recipient_email}
              onChange={v => setForm(f => ({ ...f, recipient_email: v }))}
              placeholder="marc@a16z.com" />
            <Input label="COMPANY / FUND" testid="pl-recipient-company"
              value={form.recipient_company}
              onChange={v => setForm(f => ({ ...f, recipient_company: v }))}
              placeholder="Andreessen Horowitz" />
            <div className="grid grid-cols-2 gap-3">
              <Input label="EXPIRES IN (DAYS)" type="number" testid="pl-expires-days"
                value={form.expires_in_days}
                onChange={v => setForm(f => ({ ...f, expires_in_days: Number(v) || 7 }))} />
              <Input label="MAX VIEWS" type="number" testid="pl-max-views"
                value={form.max_views}
                onChange={v => setForm(f => ({ ...f, max_views: Number(v) || 5 }))} />
            </div>
            <div className="md:col-span-2">
              <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 block mb-1">
                INTERNAL NOTES (OPTIONAL)
              </span>
              <textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Met at SaaStr · interested in the mining wedge"
                data-testid="pl-notes"
                className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000] resize-none"
              />
            </div>
            <div className="md:col-span-2 flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)}
                className="border border-zinc-800 hover:border-zinc-600 font-mono text-[11px] uppercase tracking-widest px-4 py-2 text-zinc-400">
                CANCEL
              </button>
              <button type="submit" disabled={submitting}
                data-testid="pl-submit"
                className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-2 hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
                <LinkIcon className="w-3.5 h-3.5" />
                {submitting ? "MINTING…" : "MINT + COPY URL"}
              </button>
            </div>
          </form>
        </Panel>
      )}

      {/* LIST */}
      <Panel title="// ALL LINKS" testId="pitch-links-list">
        {loading && (
          <div className="p-6 text-center font-mono text-xs text-zinc-600">LOADING…</div>
        )}
        {!loading && links.length === 0 && (
          <div className="p-8 text-center">
            <ShieldCheck className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
            <div className="font-mono text-xs text-zinc-500">
              No investor links minted yet.
            </div>
            <div className="font-mono text-[10px] text-zinc-600 mt-1">
              Click NEW LINK to create your first one.
            </div>
          </div>
        )}
        <div className="divide-y divide-zinc-900">
          {links.map(link => {
            const status = deriveStatus(link);
            const expanded = expandedId === link.id;
            return (
              <div key={link.id} data-testid={`pitch-link-row-${link.id}`} className="p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-bold text-base tracking-tight">
                        {link.recipient_name}
                      </span>
                      <span className={`px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest border ${STATUS_COLOR[status]}`}>
                        {status}
                      </span>
                    </div>
                    <div className="font-mono text-[10px] text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      <Mail className="w-3 h-3" />
                      <span>{link.recipient_email}</span>
                      {link.recipient_company && (
                        <>
                          <span>·</span>
                          <Building2 className="w-3 h-3" />
                          <span>{link.recipient_company}</span>
                        </>
                      )}
                    </div>
                    <div className="font-mono text-[10px] text-zinc-600 mt-1.5">
                      Created {fmtDate(link.created_at)}
                      {link.created_by_email && <> · by {link.created_by_email}</>}
                      {" · "}Expires {fmtDate(link.expires_at)}
                      {" · "}{link.view_count}/{link.max_views} views
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => copyUrl(link.token, link.recipient_name)}
                      data-testid={`pl-copy-${link.id}`}
                      className="border border-zinc-800 hover:border-[#FFB000] hover:text-[#FFB000] px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-300 flex items-center gap-1.5"
                    >
                      <Copy className="w-3 h-3" /> COPY URL
                    </button>
                    <button
                      onClick={() => setExpandedId(expanded ? null : link.id)}
                      data-testid={`pl-expand-${link.id}`}
                      className="border border-zinc-800 hover:border-zinc-600 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5"
                    >
                      <Eye className="w-3 h-3" /> {expanded ? "HIDE" : "AUDIT"}
                    </button>
                    {status === "active" && (
                      <button
                        onClick={() => revoke(link.token, link.recipient_name)}
                        data-testid={`pl-revoke-${link.id}`}
                        className="border border-[#FF6B47]/40 text-[#FF6B47] hover:bg-[#FF6B47]/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest flex items-center gap-1.5"
                      >
                        <X className="w-3 h-3" /> REVOKE
                      </button>
                    )}
                    <button
                      onClick={() => remove(link.token, link.recipient_name)}
                      data-testid={`pl-delete-${link.id}`}
                      title="Hard-delete (wipes audit log)"
                      className="border border-zinc-900 hover:border-[#FF3B30]/40 text-zinc-600 hover:text-[#FF3B30] px-2 py-1.5 font-mono text-[10px]"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                {/* Audit log */}
                {expanded && (
                  <div className="mt-3 pt-3 border-t border-zinc-900 space-y-2" data-testid={`pl-audit-${link.id}`}>
                    {link.notes && (
                      <div className="font-mono text-[10px] text-zinc-400 leading-relaxed border-l-2 border-zinc-800 pl-2">
                        <span className="text-zinc-600">NOTES · </span>{link.notes}
                      </div>
                    )}
                    <div>
                      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">ACCESS LOG</div>
                      {(link.access_log || []).length === 0 ? (
                        <div className="font-mono text-[10px] text-zinc-600">No views yet.</div>
                      ) : (
                        <div className="space-y-1">
                          {(link.access_log || []).slice().reverse().map((a, i) => (
                            <div key={i} className="font-mono text-[10px] text-zinc-400 flex items-start gap-2">
                              <ChevronRight className="w-3 h-3 text-[#FFB000] mt-0.5 flex-shrink-0" />
                              <div>
                                <div>{fmtDate(a.viewed_at)}{a.ip ? ` · ${a.ip}` : ""}</div>
                                {a.user_agent && (
                                  <div className="text-zinc-600 truncate max-w-2xl" title={a.user_agent}>
                                    {a.user_agent.slice(0, 100)}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Panel>

      {/* SECURITY NOTE */}
      <div className="border-l-2 border-[#FFB000] pl-3 py-2">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-[#FFB000] mt-0.5 flex-shrink-0" />
          <div className="font-mono text-[10px] text-zinc-500 leading-relaxed">
            Every PDF generated through these links is watermarked with the
            recipient's email + access date in the footer AND a faint diagonal
            stamp across every slide. Designed to discourage forwarding rather
            than prevent it absolutely — pair with a written NDA for VC pitches.
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent, sub }) {
  const cls = accent === "red" ? "text-[#FF3B30]"
    : accent === "green" ? "text-[#00E676]"
    : accent === "amber" ? "text-[#FFB000]" : "text-white";
  return (
    <div className="bg-black p-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-mono text-2xl font-medium mt-1 ${cls}`}>{value}</div>
      {sub && <div className="font-mono text-[9px] text-zinc-600 mt-1">{sub}</div>}
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder = "", testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
      />
    </label>
  );
}
